/* eslint-disable max-params */
import type { ChatSessionModel, ScholarState } from "../models"

import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/**
 * QUERY: Gets a single session by ID
 * @param sessionId - The session ID
 * @param idToken - Optional authentication token
 * @returns Session model
 * @category Query
 */
export async function getSessionApi(
  sessionId: string,
  idToken?: string
): Promise<ChatSessionModel> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/get-session?session_id=${sessionId}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return await response.json()
}

/**
 * QUERY: Gets agent state for an Explorer session
 * @param sessionId - The session ID
 * @param idToken - Optional authentication token
 * @returns Agent state object
 * @category Query
 */
export async function getAgentStateApi(
  sessionId: string,
  idToken?: string
): Promise<{ agent_state: ScholarState }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/get-agent-state`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({ session_id: sessionId }),
    },
    idToken
  )

  return await response.json()
}

/**
 * MUTATION: Cancels a queued request
 * @param requestId - The queue request ID
 * @param idToken - Optional authentication token
 * @category Mutation
 */
export async function cancelQueueRequestApi(
  requestId: string,
  idToken?: string
): Promise<Response> {
  return fetchWithAuthRetry(
    `${BACKEND_URI}/cancel-request/${requestId}`,
    {
      method: "POST",
      headers: getHeaders(idToken),
    },
    idToken
  )
}

/**
 * MUTATION: Creates a new chat session
 * @param title - Session title
 * @param sessionType - Type of session to create
 * @param customTitle - Optional user-provided title override
 * @param sensitiveData - Whether session contains sensitive data
 * @param sessionId - Optional existing session ID to reuse
 * @param idToken - Optional authentication token
 * @param projectId - Optional project ID to associate with session
 * @param sourceSessionId - Optional source session id (branching/handoff)
 * @param sourceMessageId - Optional source message id (branching/handoff)
 * @param iconType - Optional icon hint for the new session
 * @returns Created session model
 * @category Mutation
 */
export async function createSessionApi(
  title: string,
  sessionType: string,
  customTitle?: string,
  sensitiveData?: boolean,
  sessionId?: string,
  idToken?: string,
  projectId?: string,
  sourceSessionId?: string,
  sourceMessageId?: string,
  iconType?: string,
  // HERMES (B6): binds the session to a hosted agent AT CREATION ONLY. The
  // server validates the agent (enabled + AccessPolicy, uniform 404), derives
  // the session `visibility` from the agent's `memory_mode` server-side, and
  // refuses (409) to bind a pre-existing unbound session id. Turns are posted
  // as usual — never send the agent id per turn.
  hostedAgentId?: string
): Promise<ChatSessionModel> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/create-session`,
    {
      method: "POST",
      body: JSON.stringify({
        title,
        session_type: sessionType,
        custom_title: customTitle,
        sensitive_data: sensitiveData,
        session_id: sessionId,
        project_id: projectId,
        source_session_id: sourceSessionId,
        source_message_id: sourceMessageId,
        icon_type: iconType,
        hosted_agent_id: hostedAgentId,
      }),
      headers: getHeaders(idToken),
    },
    idToken
  )

  return await response.json()
}

/**
 * MUTATION: Creates a new parser session
 * @param title - Session title
 * @param idToken - Optional authentication token
 * @returns Created session model
 * @category Mutation
 */
export async function createParserSessionApi(
  title: string,
  templateId?: string,
  idToken?: string
): Promise<ChatSessionModel> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/create-parser-session`,
    {
      method: "POST",
      body: JSON.stringify({
        custom_title: title,
        template_id: templateId,
      }),
      headers: getHeaders(idToken),
    },
    idToken
  )

  return await response.json()
}

/**
 * MUTATION: Accept a dynamic title suggestion
 * @param sessionId - The session ID
 * @param title - The new title to accept
 * @param idToken - Optional authentication token
 * @returns Success response
 * @category Mutation
 */
export async function acceptTitleSuggestion(
  sessionId: string,
  title: string,
  idToken?: string
): Promise<{
  success: boolean
  session_id: string
  title: string
  title_state_changed?: {
    session_id: string
    title_updated?: { new_title: string; auto_accepted?: boolean }
    pending_title_suggestion?: null
  }
}> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/accept-title-suggestion`,
    {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        title: title,
      }),
      headers: getHeaders(idToken),
    },
    idToken
  )

  return await response.json()
}

/**
 * MUTATION: Dismiss a dynamic title suggestion
 * @param sessionId - The session ID
 * @param idToken - Optional authentication token
 * @returns Success response
 * @category Mutation
 */
export async function dismissTitleSuggestion(
  sessionId: string,
  idToken?: string
): Promise<{
  success: boolean
  session_id: string
  title_state_changed?: {
    session_id: string
    pending_title_suggestion?: null
  }
}> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/dismiss-title-suggestion`,
    {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
      }),
      headers: getHeaders(idToken),
    },
    idToken
  )

  return await response.json()
}
/**
 * QUERY: Lists sessions for a session type, optionally filtered by project
 * @param sessionType - Type of sessions to list
 * @param idToken - Optional authentication token
 * @param projectId - Optional project ID to filter by
 * @returns Array of session models
 * @category Query
 */
export async function listSessionsApi(
  sessionType: string,
  idToken?: string,
  projectId?: string,
  signal?: AbortSignal
): Promise<ChatSessionModel[]> {
  const query = new URLSearchParams({ session_type: sessionType })
  if (projectId) query.set("project_id", projectId)
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/list-sessions?${query.toString()}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
      signal,
    },
    idToken
  )

  return await response.json()
}

/**
 * QUERY: Lists sessions for multiple session types in a single request
 * @param sessionTypes - Array of session types to include
 * @param idToken - Optional authentication token
 * @returns Array of session models
 * @category Query
 */
export async function listSessionsBatchApi(
  sessionTypes: string[],
  idToken?: string,
  signal?: AbortSignal
): Promise<ChatSessionModel[]> {
  const params = new URLSearchParams()
  sessionTypes.forEach((t) => params.append("session_types", t))

  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/list-sessions-batch?${params.toString()}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
      signal,
    },
    idToken
  )

  return await response.json()
}
